/**
 * Contratos API module.
 *
 * Covers contract CRUD and file (archivo) management.
 *
 * All functions delegate to `apiFetch`, which handles auth headers,
 * JSON parsing and Ant Design error/success toasts automatically.
 */

import { apiFetch } from "./apiClient";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ContratoArchivo {
  provider: string;
  key: string;
  url: string;
  originalName: string;
  mimeType: string;
  size: number;
}

/** Categoría del documento:
 * - "contrato": documento legal, siempre vinculado a una propiedad.
 * - "otro": cualquier otro documento que el admin quiera entregar al cliente
 *   (manuales, certificados, facturas, etc.); la propiedad es opcional.
 */
export type ContratoTipo = "contrato" | "otro";

export interface Contrato {
  id: number;
  titulo: string;
  descripcion: string | null;
  fecha: string | null;
  estado: "borrador" | "vigente" | "vencido";
  tipo: ContratoTipo;
  id_cliente: number;
  id_propiedad: number | null;
  archivos: ContratoArchivo[];
  cliente?: { id: number; nombre_usuario: string };
  propiedad?: { id: number; nombrepropiedad: string };
  createdAt: string;
  updatedAt: string;
}

export type ContratoInput = Pick<
  Contrato,
  | "titulo"
  | "descripcion"
  | "fecha"
  | "estado"
  | "tipo"
  | "id_cliente"
  | "id_propiedad"
>;

// ─── API response wrappers ───────────────────────────────────────────────────

interface SuccessResponse<T> {
  success: boolean;
  data: T;
}

// ─── contratosApi ────────────────────────────────────────────────────────────

export const contratosApi = {
  // ── CRUD ──────────────────────────────────────────────────────────────────

  /** GET /contratos — list all contracts (filtered server-side by role). */
  getAll: async (): Promise<Contrato[]> => {
    const res = await apiFetch<SuccessResponse<Contrato[]>>("/contratos", {
      errorMsg: "Error al obtener contratos",
    });
    return res?.data ?? [];
  },

  /** GET /contratos/:id */
  getById: async (id: number): Promise<Contrato | null> => {
    const res = await apiFetch<SuccessResponse<Contrato>>(`/contratos/${id}`, {
      errorMsg: "Error al obtener el contrato",
    });
    return res?.data ?? null;
  },

  /** POST /contratos */
  create: async (data: ContratoInput): Promise<Contrato | null> => {
    const res = await apiFetch<SuccessResponse<Contrato>>("/contratos", {
      method: "POST",
      body: JSON.stringify(data),
      successMsg: "Contrato creado correctamente",
      errorMsg: "Error al crear el contrato",
    });
    return res?.data ?? null;
  },

  /** PUT /contratos/:id */
  update: async (
    id: number,
    data: Partial<ContratoInput>,
  ): Promise<Contrato | null> => {
    const res = await apiFetch<SuccessResponse<Contrato>>(`/contratos/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
      successMsg: "Contrato actualizado correctamente",
      errorMsg: "Error al actualizar el contrato",
    });
    return res?.data ?? null;
  },

  /** DELETE /contratos/:id */
  delete: async (id: number): Promise<boolean> => {
    const res = await apiFetch<{ success: boolean }>(`/contratos/${id}`, {
      method: "DELETE",
      successMsg: "Contrato eliminado correctamente",
      errorMsg: "Error al eliminar el contrato",
    });
    return res?.success ?? false;
  },

  // ── Archivos ─────────────────────────────────────────────────────────────

  /** POST /contratos/:id/archivos — upload via FormData (multipart). */
  uploadArchivo: async (
    id: number,
    file: File,
  ): Promise<ContratoArchivo | null> => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await apiFetch<SuccessResponse<ContratoArchivo>>(
      `/contratos/${id}/archivos`,
      {
        method: "POST",
        body: formData,
        successMsg: "Archivo subido correctamente",
        errorMsg: "Error al subir el archivo",
      },
    );
    return res?.data ?? null;
  },

  /** DELETE /contratos/:id/archivos?key=... */
  deleteArchivo: async (
    id: number,
    key: string,
  ): Promise<ContratoArchivo[]> => {
    const res = await apiFetch<SuccessResponse<ContratoArchivo[]>>(
      `/contratos/${id}/archivos?key=${encodeURIComponent(key)}`,
      {
        method: "DELETE",
        successMsg: "Archivo eliminado correctamente",
        errorMsg: "Error al eliminar el archivo",
      },
    );
    return res?.data ?? [];
  },
};
